import { useEffect, useState, useMemo } from "react";
import DeckGL, { MapController } from "deck.gl";
import { MapView, WebMercatorViewport } from "@deck.gl/core";

import { renderAllLayers } from "./render_layers";

import Colors from "./colors.json";

const Years = ["average", "2024", "2023", "2022", "2021", "2020", "2019", "2018", "2017", "2016", "2015", "2014", "2013", "2012", "2011"];
const Distances = [0, 1000, 2000, 3000];

const JapanBounds = [
  [122.89305980289718, 23.977964821892858],
  [145.9483624174012, 45.64257950403767]
];

const App = () => {
  // 地図画面の大きさ
  const mapSize = () => {
    const topbar_height = 60;

    return {
      width: window.innerWidth,
      height: window.innerHeight - topbar_height
    };
  };

  // 日本全国fitzoneするときのViewState
  const fitViewState = () => {
    const {width, height} = mapSize();

    const viewport = new WebMercatorViewport({
      width,
      height,
    });
    const {longitude, latitude, zoom} = viewport.fitBounds(JapanBounds);

    return {
      width,
      height,
      longitude: longitude,
      latitude: latitude,
      zoom: zoom - 0.25,
      minZoom: 3,
      maxZoom: 17,
      pitch: 45,
    };
  };

  // InitViewState
  const viewstateInit = fitViewState();
  const [viewState, setViewState] = useState(viewstateInit);

  // mapの繰り返し
  const views = useMemo(() => new MapView({ repeat: true }), []);

  // map移動時
  const onViewStateChange = ({viewState}) => {
    setViewState(viewState);
  };

  // ウィンドウリサイズ
  const handleResize = () => {
    const {width, height} = mapSize();

    setViewState((prev) => ({
      ...prev,
      width,
      height
    }));
  };
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // year
  const [yearIndex, setYearIndex] = useState(0);
  const onChangeYear = ({target}) => {
    setYearIndex(target.value);
  };

  // data
  const [data, setData] = useState(null);
  useEffect (() => {
    const year = Years[yearIndex];
    Promise.all([
      fetch(`/data/${year}/0.json`).then((res) => res.json()),
      fetch(`/data/${year}/1000.json`).then((res) => res.json()),
      fetch(`/data/${year}/2000.json`).then((res) => res.json()),
      fetch(`/data/${year}/3000.json`).then((res) => res.json()),
    ]).then(
      (dataAll) => setData(dataAll)
    );
  }, [yearIndex]);

  const [distanceCheck, setDistanceCheck] = useState([true, true, true, true]);

  // layers
  const layers = useMemo(() => {
    if (!data) return renderAllLayers({data: null});

    return renderAllLayers({
      data: data,
      visible: distanceCheck,
    });

  }, [distanceCheck, data]);

  // fitzoneHandler
  const fitzoneHandler = () => {
    setViewState(fitViewState());
  };

  return (
    <div className="App">
      <div className="map-container">
        <DeckGL
          layers={layers}
          initialViewState={viewstateInit}
          viewState={viewState}
          views={views}
          controller={{ type: MapController, dragRotate: false }}
          onViewStateChange={onViewStateChange}
          /* getCursor={() => cursor} */
          /* onHover={hoverMapHandler} */
          /* onClick={clickMapHandler} */
        />
      </div>

      <div className="map-attribution">
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noreferrer"
        >
          ©︎ OpenStreetMap contributors
        </a>
      </div>

      <div className="top-panel panel">
        {/* year-select */}
        <div className="topbar-year-controls">
          <span className="label">Year</span>
          <select
            className="year-select"
            value={yearIndex}
            onChange={onChangeYear}
          >
            {
              Years.map((y, i) => (
                <option key={"select-year-" + y} value={i}>
                  {y}
                </option>
              ))
            }
          </select>
        </div>

        {/* distance-checkbox */}
        <div className="distance-group">
          <span className="label">Distance From Station</span>
          <div className="distance-checkbox-wrapper">
            {
              Distances.map((v, i) => {
                const color = Colors[v];
                const rgba = color.join(",");

                return (
                  <label
                    key={i}
                    className="distance-checkbox-item"
                    style={{ "--item-color": `rgba(${rgba})` }}
                  >
                    <input
                      type="checkbox"
                      checked={distanceCheck[i]}
                      onChange={
                        (e) => setDistanceCheck((v) => {
                          const next = [...v];
                          next[i] = e.target.checked;
                          return next;
                        })
                      }
                    />
                    <span className="color-box"></span>
                    <span>{v === 0 ? `${v} m (station)` : `${v} m`}</span>
                  </label>
                );
              })
            }
          </div>
        </div>

        {/* fit-zone */}
        <div className="topbar-fitzone-controls">
          <button
            className="fit-zone-button"
            type="button"
            title="Fit Zone"
            onClick={fitzoneHandler}
          >
            Fit Zone
          </button>
        </div>
      </div>
    </div>
  );
};

export default App;
